(function () {
  'use strict';

  const COLOR_HEX = {
    'Gray': '#7a7a7a',
    'Grey': '#7a7a7a',
    'Black': '#1a1a1a',
    'White': '#ececec',
    'Desert Tan': '#cfb487',
    'OD Green': '#7c9543',
    'Blue': '#4d7ee0',
    'Red': '#d65555',
    'Teal': '#76bcc7',
    'Yellow': '#f8c020',
    'Pink': '#d77a9e',
    'Orange': '#f59546',
    'Purple': '#8460f0',
    'Lime Green': '#82c057',
    'Seafoam': '#8be0b4',
    'White/Black': '#1a1a1a'
  };

  const STANDARD_COLORS = new Set(['Gray', 'Grey', 'Black', 'White', 'White/Black']);

  function isStandardColor(name) {
    return STANDARD_COLORS.has(name);
  }

  function isStandardColorForProduct(name, productHandle) {
    if (productHandle === 'insulated-dog-condo' && name === 'Desert Tan') return true;
    return isStandardColor(name);
  }

  function formatMoney(cents) {
    const dollars = (cents || 0) / 100;
    return dollars.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    });
  }

  function formatMoneyDecimal(cents) {
    const dollars = (cents || 0) / 100;
    return dollars.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function discountedCents(variant) {
    const pct = parseFloat(variant.discount_percent) || 0;
    if (pct <= 0) return variant.price;
    return Math.round(variant.price * (1 - pct / 100));
  }

  function findOptionIndex(product, optionName) {
    if (!product.options) return -1;
    const lower = optionName.toLowerCase();
    for (let i = 0; i < product.options.length; i++) {
      if ((product.options[i].name || '').toLowerCase() === lower) return i;
    }
    return -1;
  }

  function productHasSize(product) {
    return findOptionIndex(product, 'Size') !== -1;
  }

  function getSizeValues(product) {
    const idx = findOptionIndex(product, 'Size');
    if (idx === -1) return [];
    return product.options[idx].values || [];
  }

  function getColorValues(product) {
    const idx = findOptionIndex(product, 'Color');
    if (idx === -1) return [];
    return product.options[idx].values || [];
  }

  function variantOptionFor(product, variant, optionName) {
    const idx = findOptionIndex(product, optionName);
    if (idx === -1) return null;
    return variant['option' + (idx + 1)];
  }

  function findVariant(product, size, color) {
    const hasSize = productHasSize(product);
    return product.variants.find(v => {
      const vSize = hasSize ? variantOptionFor(product, v, 'Size') : null;
      const vColor = variantOptionFor(product, v, 'Color');
      if (hasSize && vSize !== size) return false;
      if (vColor !== color) return false;
      return true;
    });
  }

  function cheapestVariant(product) {
    let cheap = null;
    product.variants.forEach(v => {
      if (!v.available) return;
      if (!cheap || v.price < cheap.price) cheap = v;
    });
    return cheap || product.variants[0];
  }

  function filterMediaByColor(product, color) {
    if (!product.media || product.media.length === 0) return [];
    const target = (color || '').toLowerCase().trim();
    const matched = [];   // media tagged for the selected color
    const unscoped = [];  // generic shots with no #color_ tag

    product.media.forEach(function (m) {
      const lowerAlt = (m.alt || '').toLowerCase();
      const tagIdx = lowerAlt.indexOf('#color_');
      if (tagIdx === -1) {
        unscoped.push(m); // no color tag — generic product shot, always a fallback
        return;
      }
      // everything after "#color_" (7 chars), split on commas for multi-color tags
      const tagValue = lowerAlt.slice(tagIdx + 7).trim();
      const colors = tagValue.split(',').map(function (s) { return s.trim(); });
      if (target && colors.indexOf(target) !== -1) {
        matched.push(m);
      }
      // tagged for a different color → excluded
    });

    if (matched.length === 0) return unscoped.length ? unscoped : product.media.slice();
    return matched.concat(unscoped);
  }

  function extractSizeFromBreedString(rawSize) {
    if (!rawSize) return null;
    const match = rawSize.match(/^\s*([0-9]+("|″)?\+?)/);
    if (!match) return null;
    return match[1].replace(/″/g, '"');
  }

  function normalizeSize(s) {
    if (!s) return '';
    return String(s).replace(/″/g, '"').trim();
  }

  function PDPConfigurator(root) {
    this.root = root;
    const dataEl = document.getElementById('pdp-configurator-data');
    if (!dataEl) {
      console.warn('[PDP] No data element found');
      return;
    }
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[PDP] Failed to parse data JSON', e);
      return;
    }
    if (!this.data.products || this.data.products.length === 0) {
      console.warn('[PDP] No products configured');
      return;
    }

    this.crateBlockByHandle = {};
    (this.data.crate_blocks || []).forEach(b => {
      if (b.handle) this.crateBlockByHandle[b.handle] = b;
    });

    this.state = {
      productIndex: 0,
      size: null,
      color: null,
      addons: {},
      paymentMethod: 'full',
      activeMediaIndex: 0
    };

    this.breeds = null;
    this.loadBreeds();
    this.bindElements();
    this.bindEvents();
    this.selectInitialProduct();
  }

  PDPConfigurator.prototype.bindElements = function () {
    const q = (sel) => this.root.querySelector(sel);
    this.el = {
      mainImage: q('[data-pdp-main-image]'),
      thumbs: q('[data-pdp-thumbs]'),
      crateGrid: q('[data-pdp-crate-grid]'),
      notFor: q('[data-pdp-not-for]'),
      haStrip: q('[data-pdp-ha-strip]'),
      premiumStrip: q('[data-pdp-premium-strip]'),
      stepSize: q('[data-pdp-step-size]'),
      stepColor: q('[data-pdp-step-color]'),
      stepAddons: q('[data-pdp-step-addons]'),
      sizeList: q('[data-pdp-size-list]'),
      colorStandard: q('[data-pdp-color-standard]'),
      colorPremium: q('[data-pdp-color-premium]'),
      colorStandardList: q('[data-pdp-color-standard-list]'),
      colorPremiumList: q('[data-pdp-color-premium-list]'),
      colorPromo: q('[data-pdp-color-promo]'),
      addons: q('[data-pdp-addons]'),
      payOptions: q('[data-pdp-pay-options]'),
      cartLines: q('[data-pdp-cart-lines]'),
      cartMsrp: q('[data-pdp-cart-msrp]'),
      cartTotal: q('[data-pdp-cart-total]'),
      cartSavings: q('[data-pdp-cart-savings]'),
      atcBtn: q('[data-pdp-atc-btn]'),
      atcLabel: q('[data-pdp-atc-label]'),
      cartError: q('[data-pdp-cart-error]'),
      breedInput: q('[data-pdp-breed-input]'),
      breedBtn: q('[data-pdp-breed-btn]'),
      breedResult: q('[data-pdp-breed-result]'),
      category: q('[data-pdp-category]'),
      rating: q('[data-pdp-rating]'),
      title: q('[data-pdp-title]'),
      desc: q('[data-pdp-desc]'),
      priceCurrent: q('[data-pdp-price-current]'),
      priceMsrp: q('[data-pdp-price-msrp]'),
      savePill: q('[data-pdp-save-pill]'),
      financing: q('[data-pdp-financing]'),
      shippingFill: q('[data-pdp-shipping-fill]'),
      shippingLabel: q('[data-pdp-shipping-label]'),
      shippingThreshold: q('[data-pdp-shipping-threshold]'),
      trustShipping: q('[data-pdp-trust-shipping]'),
      imageTint: q('[data-pdp-image-tint]'),
      imageCaption: q('[data-pdp-image-caption]'),
      rentStrip: q('[data-pdp-rent-strip]'),
      cartFi: q('[data-pdp-cart-fi]'),
      cartFiIcon: q('[data-pdp-cart-fi-icon]'),
      cartTotalLabel: q('[data-pdp-cart-total-label]'),
      cartMicro: q('[data-pdp-cart-micro]')
    };
  };

  // Short, marketing-friendly crate names by handle
  const CRATE_SHORT_NAMES = {
    'collapsible-dog-crate':      'Collapsible',
    'high-anxiety-crate-xl':      'High Anxiety',
    'stationary-dog-crate':       'Stationary',
    'mini-stationary-dog-crate':  'Mini Stationary',
    'airline-dog-crate':          'Airline',
    'the-impact-den':             'Dog Den',
    'insulated-dog-condo':        'Condo'
  };

  function shortCrateName(product) {
    if (!product) return '';
    if (CRATE_SHORT_NAMES[product.handle]) return CRATE_SHORT_NAMES[product.handle];
    return (product.title || '')
      .replace(/\bDog\s+Crate\b/gi, '')
      .replace(/\bInsulated\b/gi, '')
      .replace(/\bImpact\b/gi, '')
      .replace(/\bDog\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Read review metafield data from the product JSON
  function ratingFor(product) {
    const r = (product && product.reviews) || {};
    const rating = parseFloat(r.rating) || 0;
    const count = parseInt(r.count, 10) || 0;
    return { rating: rating, count: count };
  }

  function starsHtml(value) {
    const full = Math.floor(value);
    const half = (value - full) >= 0.5 ? 1 : 0;
    const empty = 5 - full - half;
    let out = '';
    for (let i = 0; i < full; i++) out += '★';
    if (half) out += '★';
    for (let i = 0; i < empty; i++) out += '☆';
    return out;
  }

  PDPConfigurator.prototype.bindEvents = function () {
    const self = this;
    this.el.atcBtn.addEventListener('click', function () { self.addToCart(); });
    this.el.breedBtn.addEventListener('click', function (e) {
      e.preventDefault();
      self.handleBreedSearch();
    });
    this.el.breedInput.addEventListener('keypress', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); self.handleBreedSearch(); }
    });

    window.addEventListener('crate:changed', function (e) {
      const handle = e.detail && e.detail.productHandle;
      if (!handle) return;
      const idx = self.data.products.findIndex(p => p.handle === handle);
      if (idx !== -1 && idx !== self.state.productIndex) {
        self.selectProduct(idx);
      }
    });
  };

  PDPConfigurator.prototype.loadBreeds = function () {
    const url = this.data.settings.breed_data_url;
    if (!url) return;
    const self = this;
    fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject('breed fetch failed'))
      .then(data => { self.breeds = data; })
      .catch(err => console.warn('[PDP] Breed data load failed:', err));
  };

  PDPConfigurator.prototype.selectInitialProduct = function () {
    let idx = 0;
    const defaultHandle = this.data.settings.default_product_handle;
    if (defaultHandle) {
      const found = this.data.products.findIndex(p => p.handle === defaultHandle);
      if (found !== -1) idx = found;
    }
    this.selectProduct(idx, { silent: true });
    this.renderCrateGrid();
    this.renderAddons();
    this.renderPayOptions();
  };

  PDPConfigurator.prototype.currentProduct = function () {
    return this.data.products[this.state.productIndex];
  };

  PDPConfigurator.prototype.selectProduct = function (idx, opts) {
    opts = opts || {};
    if (idx < 0 || idx >= this.data.products.length) return;
    this.state.productIndex = idx;
    const product = this.currentProduct();

    const sizes = getSizeValues(product);
    const colors = getColorValues(product);

    if (sizes.length > 0) {
      const cheap = cheapestVariant(product);
      this.state.size = cheap ? variantOptionFor(product, cheap, 'Size') : sizes[0];
    } else {
      this.state.size = null;
    }
    this.state.color = colors[0] || null;
    this.state.activeMediaIndex = 0;

    this.autoMatchAddonSizes();
    this.renderAll();

    if (!opts.silent) {
      window.dispatchEvent(new CustomEvent('crate:changed', {
        detail: { productHandle: product.handle }
      }));
    }
  };

  PDPConfigurator.prototype.renderAll = function () {
    this.renderInfo();
    this.renderCrateGrid();
    this.renderNotFor();
    this.renderEduStrips();
    this.renderSizeStep();
    this.renderColorStep();
    this.renderAddons();
    this.renderPayOptions();
    this.renderGallery();
    this.renderCart();
    this.renderTrustShipping();
  };

  PDPConfigurator.prototype.focusRenderedControl = function (attribute, value) {
    const expected = String(value);
    const control = Array.prototype.find.call(
      this.root.querySelectorAll('[' + attribute + ']'),
      function (candidate) { return candidate.getAttribute(attribute) === expected; }
    );
    if (control) control.focus();
  };

  PDPConfigurator.prototype.renderInfo = function () {
    const product = this.currentProduct();
    const block = this.crateBlockByHandle[product.handle] || {};
    const variant = this.currentVariant();
    const rating = ratingFor(product);

    if (block.category) {
      this.el.category.textContent = block.category;
      this.el.category.hidden = false;
      this.el.category.classList.toggle('pdp-config__category-badge--guarantee', block.damage_guaranteed === true);
    } else {
      this.el.category.hidden = true;
    }

    if (rating.rating > 0 && rating.count > 0) {
      this.el.rating.setAttribute('aria-label', 'Rated ' + rating.rating.toFixed(1) + ' out of 5 from ' + rating.count.toLocaleString('en-US') + ' reviews');
      this.el.rating.innerHTML =
        '<span class="pdp-config__rating-stars" aria-hidden="true">' + starsHtml(rating.rating) + '</span>' +
        '<span class="pdp-config__rating-value">' + rating.rating.toFixed(1) + '</span>' +
        '<span class="pdp-config__rating-count">(' + rating.count.toLocaleString('en-US') + ' reviews)</span>';
      this.el.rating.hidden = false;
    } else {
      this.el.rating.removeAttribute('aria-label');
      this.el.rating.innerHTML = '';
      this.el.rating.hidden = true;
    }

    this.el.title.textContent = shortCrateName(product);
    this.el.desc.textContent = product.description || '';

    const current = variant ? discountedCents(variant) : 0;
    const msrp = variant ? variant.price : 0;
    const savings = msrp - current;
    const pct = variant ? parseFloat(variant.discount_percent) || 0 : 0;
    const hasDiscount = !!variant && current !== msrp && savings > 0;

    this.el.priceCurrent.textContent = formatMoney(current);
    if (hasDiscount) {
      this.el.priceMsrp.innerHTML = '<s>' + formatMoney(msrp) + '</s>';
      this.el.priceMsrp.style.display = '';
      this.el.savePill.textContent = 'Save ' + formatMoney(savings) + ' · ' + Math.round(pct) + '% Off';
      this.el.savePill.hidden = false;
      this.el.savePill.style.display = '';
    } else {
      this.el.priceMsrp.innerHTML = '';
      this.el.priceMsrp.style.display = 'none';
      this.el.savePill.textContent = '';
      this.el.savePill.hidden = true;
      this.el.savePill.style.display = 'none';
    }

    const months = this.data.settings.affirm_months || 12;
    const affirmMonthly = Math.round(current / months);
    const isRentable = block.rentable === true;
    let financing = 'or <strong style="color:var(--pdp-black)">' + escapeHtml(formatMoney(affirmMonthly)) + '/mo</strong> Affirm 0% APR';
    if (isRentable) {
      const rentMonthly = Math.round(current / 24);
      financing += ' · Rent <strong style="color:var(--pdp-highlight-blue)">' + escapeHtml(formatMoney(rentMonthly)) + '/mo</strong>';
    }
    this.el.financing.innerHTML = financing;

    const total = this.computeTotalCents();
    const threshold = (this.data.settings.free_shipping_threshold || 0) * 100;
    if (threshold > 0) {
      const pctFill = Math.min(100, Math.round((total / threshold) * 100));
      this.el.shippingFill.style.width = pctFill + '%';
      if (total >= threshold) {
        this.el.shippingLabel.innerHTML = '<span class="pdp-config__shipping-unlocked">✓ Free shipping unlocked</span>';
      } else {
        const remaining = threshold - total;
        this.el.shippingLabel.innerHTML = '<strong>' + formatMoney(remaining) + '</strong> from free shipping';
      }
      if (this.el.shippingThreshold) {
        this.el.shippingThreshold.textContent = '$' + (this.data.settings.free_shipping_threshold || 0);
      }
    } else {
      this.el.shippingFill.style.width = '0%';
      this.el.shippingLabel.textContent = '';
      if (this.el.shippingThreshold) this.el.shippingThreshold.textContent = '';
    }

    // Image tint + caption
    if (this.el.imageTint) {
      const hex = COLOR_HEX[this.state.color] || '';
      this.el.imageTint.style.background = hex
        ? 'linear-gradient(135deg, ' + hex + '00 0%, ' + hex + '24 100%)'
        : 'transparent';
    }
    if (this.el.imageCaption) {
      const parts = [shortCrateName(product)];
      if (this.state.size) parts.push(this.state.size);
      if (this.state.color) parts.push(this.state.color);
      this.el.imageCaption.textContent = parts.join(' · ');
    }
  };

  PDPConfigurator.prototype.renderTrustShipping = function () {
    if (!this.el.trustShipping) return;
    const threshold = this.data.settings.free_shipping_threshold || 0;
    this.el.trustShipping.textContent = threshold > 0 ? 'On orders $' + threshold + '+' : 'On all orders';
  };

  PDPConfigurator.prototype.renderNotFor = function () {
    const product = this.currentProduct();
    const block = this.crateBlockByHandle[product.handle] || {};
    const notFor = typeof block.not_for === 'string' ? block.not_for.trim() : '';
    if (notFor) {
      this.el.notFor.innerHTML = '<span><strong>Heads up:</strong> ' + escapeHtml(notFor) + '</span>';
      this.el.notFor.hidden = false;
    } else {
      this.el.notFor.hidden = true;
    }
  };

  PDPConfigurator.prototype.renderEduStrips = function () {
    const product = this.currentProduct();
    const block = this.crateBlockByHandle[product.handle] || {};
    if (this.el.haStrip) {
      this.el.haStrip.hidden = block.damage_guaranteed !== true;
    }
    if (this.el.premiumStrip) {
      this.el.premiumStrip.hidden = !this.state.color || isStandardColorForProduct(this.state.color, product.handle);
    }
  };

  PDPConfigurator.prototype.renderCrateGrid = function () {
    const self = this;
    const items = this.data.products.map(function (product, idx) {
      const block = self.crateBlockByHandle[product.handle] || {};
      const cheap = cheapestVariant(product);
      const startCents = cheap ? discountedCents(cheap) : 0;
      const selected = idx === self.state.productIndex;
      const tagline = block.tagline || '';
      const badge = block.badge || '';
      const isGuaranteed = block.damage_guaranteed === true;
      const img = product.featured_image || '';
      const rating = ratingFor(product);
      const hasRating = rating.rating > 0 && rating.count > 0;
      const badgeText = isGuaranteed ? '✓ Damage Guaranteed' : badge;
      const badgeClass = isGuaranteed
        ? 'pdp-config__crate-badge pdp-config__crate-badge--guarantee'
        : 'pdp-config__crate-badge pdp-config__crate-badge--accent';
      const displayName = shortCrateName(product);
      return (
        '<button type="button" class="pdp-config__crate-tile' + (selected ? ' is-selected' : '') + '" data-pdp-crate-tile="' + idx + '" aria-pressed="' + (selected ? 'true' : 'false') + '">' +
          (badgeText ? '<span class="' + badgeClass + '">' + escapeHtml(badgeText) + '</span>' : '') +
          '<div class="pdp-config__crate-img-wrap">' +
            (img ? '<img loading="lazy" src="' + img + '" alt="' + escapeHtml(displayName) + '">' : '') +
          '</div>' +
          '<div class="pdp-config__crate-name">' + escapeHtml(displayName) + '</div>' +
          (tagline ? '<div class="pdp-config__crate-tagline">' + escapeHtml(tagline) + '</div>' : '') +
          '<div class="pdp-config__crate-foot">' +
            '<span class="pdp-config__crate-price">From ' + formatMoney(startCents) + '</span>' +
            (hasRating ? '<span class="pdp-config__crate-star">★ ' + rating.rating.toFixed(1) + '</span>' : '') +
          '</div>' +
        '</button>'
      );
    }).join('');
    this.el.crateGrid.innerHTML = items;
    this.el.crateGrid.querySelectorAll('[data-pdp-crate-tile]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const index = parseInt(btn.getAttribute('data-pdp-crate-tile'), 10);
        self.selectProduct(index);
        self.focusRenderedControl('data-pdp-crate-tile', index);
      });
    });
  };

  PDPConfigurator.prototype.renderSizeStep = function () {
    const product = this.currentProduct();
    if (!productHasSize(product)) {
      this.el.stepSize.style.display = 'none';
      return;
    }
    this.el.stepSize.style.display = '';

    const self = this;
    const sizes = getSizeValues(product);
    const baseVariant = cheapestVariant(product);
    const basePrice = baseVariant ? discountedCents(baseVariant) : 0;
    const selectedColor = this.state.color;

    const items = sizes.map(function (size) {
      const variant = product.variants.find(v => {
        const vSize = variantOptionFor(product, v, 'Size');
        const vColor = variantOptionFor(product, v, 'Color');
        return vSize === size && (vColor === selectedColor || !selectedColor);
      }) || product.variants.find(v => variantOptionFor(product, v, 'Size') === size);

      if (!variant) return '';
      const price = discountedCents(variant);
      const delta = price - basePrice;
      const tag = variant.sizing_shortened || '';
      const dims = variant.sizing_dimensions || '';
      const isSelected = self.state.size === size;
      const available = variant.available;
      const deltaLabel = delta > 0 ? '+' + formatMoney(delta) : (delta < 0 ? '-' + formatMoney(Math.abs(delta)) : 'Base');

      return (
        '<button type="button" class="pdp-config__size-btn' + (isSelected ? ' is-selected' : '') + (available ? '' : ' is-unavailable') + '" data-pdp-size-btn="' + escapeAttr(size) + '" aria-pressed="' + (isSelected ? 'true' : 'false') + '"' + (available ? '' : ' disabled aria-disabled="true"') + '>' +
          '<div class="pdp-config__size-num">' + escapeHtml(size) + '</div>' +
          '<div class="pdp-config__size-meta">' +
            (tag ? '<div class="pdp-config__size-line1">' + escapeHtml(tag) + '</div>' : '') +
            (dims ? '<div class="pdp-config__size-line2">' + escapeHtml(dims) + '</div>' : '') +
          '</div>' +
          '<div class="pdp-config__size-delta">' + deltaLabel + '</div>' +
        '</button>'
      );
    }).join('');

    this.el.sizeList.innerHTML = items;
    this.el.sizeList.querySelectorAll('[data-pdp-size-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.size = btn.getAttribute('data-pdp-size-btn');
        self.autoMatchAddonSizes();
        self.renderSizeStep();
        self.renderAddons();
        self.renderCart();
        self.renderInfo();
        self.focusRenderedControl('data-pdp-size-btn', self.state.size);
      });
    });
  };

  PDPConfigurator.prototype.renderColorStep = function () {
    const product = this.currentProduct();
    const colors = getColorValues(product);
    if (colors.length === 0) {
      this.el.stepColor.style.display = 'none';
      return;
    }
    if (colors.length === 1) {
      this.state.color = colors[0];
    }
    this.el.stepColor.style.display = '';

    const self = this;
    const handle = product.handle;
    const standard = colors.filter(function (c) { return isStandardColorForProduct(c, handle); });
    const premium = colors.filter(function (c) { return !isStandardColorForProduct(c, handle); });

    function renderSwatches(list, tier) {
      return list.map(function (color) {
        const hex = COLOR_HEX[color] || '#cccccc';
        const isSelected = self.state.color === color;
        const isSplit = color === 'White/Black';
        const whiteBorder = (!isSplit && /white/i.test(color)) ? ' pdp-config__swatch-block--bordered' : '';
        const splitClass = isSplit ? ' pdp-config__swatch-block--split' : '';
        const styleAttr = isSplit ? '' : (' style="background:' + hex + '"');
        return (
          '<button type="button" class="pdp-config__swatch pdp-config__swatch--' + tier + (isSelected ? ' is-selected' : '') + '" data-pdp-color-btn="' + escapeAttr(color) + '" aria-label="' + escapeAttr(color) + '" aria-pressed="' + (isSelected ? 'true' : 'false') + '">' +
            '<span class="pdp-config__swatch-block' + whiteBorder + splitClass + '"' + styleAttr + '></span>' +
            '<span class="pdp-config__swatch-label">' + escapeHtml(color) + '</span>' +
          '</button>'
        );
      }).join('');
    }

    const cfg = this.data.settings || {};
    const stdSuffix = (typeof cfg.standard_tier_suffix === 'string' && cfg.standard_tier_suffix.trim()) || '15% Off';
    const premSuffix = (typeof cfg.premium_tier_suffix === 'string' && cfg.premium_tier_suffix.trim()) || '20% Off';

    if (this.el.colorPromo) {
      const promoText = typeof cfg.color_promo_text === 'string' ? cfg.color_promo_text.trim() : '';
      if (cfg.show_color_promo !== false && promoText) {
        this.el.colorPromo.textContent = promoText;
        this.el.colorPromo.hidden = false;
      } else {
        this.el.colorPromo.textContent = '';
        this.el.colorPromo.hidden = true;
      }
    }

    const stdLabelEl = this.el.colorStandard.querySelector('.pdp-config__color-tier-label');
    const premLabelEl = this.el.colorPremium.querySelector('.pdp-config__color-tier-label');
    if (stdLabelEl) stdLabelEl.textContent = 'Standard · ' + stdSuffix;
    if (premLabelEl) premLabelEl.textContent = 'Premium · ' + premSuffix;

    if (standard.length === 0) {
      this.el.colorStandard.style.display = 'none';
    } else {
      this.el.colorStandard.style.display = '';
      this.el.colorStandardList.innerHTML = renderSwatches(standard, 'standard');
    }
    if (premium.length === 0) {
      this.el.colorPremium.style.display = 'none';
    } else {
      this.el.colorPremium.style.display = '';
      this.el.colorPremiumList.innerHTML = renderSwatches(premium, 'premium');
    }

    this.root.querySelectorAll('[data-pdp-color-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.color = btn.getAttribute('data-pdp-color-btn');
        self.state.activeMediaIndex = 0;
        self.renderColorStep();
        self.renderSizeStep();
        self.renderGallery();
        self.renderCart();
        self.renderInfo();
        self.renderEduStrips();
        self.focusRenderedControl('data-pdp-color-btn', self.state.color);
      });
    });
  };

  PDPConfigurator.prototype.renderAddons = function () {
    const self = this;
    const product = this.currentProduct();
    const addons = this.data.addons || [];
    const handle = product.handle;
    const damageDisabledHandles = { 'high-anxiety-crate-xl': true, 'the-impact-den': true, 'insulated-dog-condo': true };
    const damageDisabled = !!damageDisabledHandles[handle];

    function isDamageProtection(a) {
      return (/damage/i.test(a.title) && /protection/i.test(a.title)) ||
             (/damage/i.test(a.handle) && /protection/i.test(a.handle));
    }

    function disabledNote() {
      if (handle === 'high-anxiety-crate-xl') return 'Included with High Anxiety';
      return 'Not available for this crate';
    }

    // Auto-deselect Dog Damage Protection if this crate disables it
    if (damageDisabled) {
      addons.forEach(function (a) {
        if (isDamageProtection(a) && self.state.addons[a.handle]) delete self.state.addons[a.handle];
      });
    }

    if (addons.length === 0) {
      this.el.stepAddons.style.display = 'none';
      return;
    }
    this.el.stepAddons.style.display = '';

    const items = addons.map(function (addon) {
      const matchedVariant = self.matchAddonVariant(addon);
      const selected = !!self.state.addons[addon.handle];
      const fallback = addon.variants && addon.variants[0];
      const variantForPrice = matchedVariant || fallback;
      const msrp = variantForPrice ? variantForPrice.price : 0;
      const price = variantForPrice ? discountedCents(variantForPrice) : 0;
      const hasDiscount = msrp > price;
      const priceHtml = hasDiscount
        ? '<s class="pdp-config__addon-msrp">' + formatMoney(msrp) + '</s> +' + formatMoney(price)
        : '+' + formatMoney(price);
      const name = addon.title || '';
      const override = typeof addon.desc_override === 'string' ? addon.desc_override.trim() : '';
      const desc = override || addon.description || '';
      const isDisabled = damageDisabled && isDamageProtection(addon);
      const note = isDisabled ? disabledNote() : '';
      const img = addon.featured_image || addon.image || '';
      const classes = 'pdp-config__addon' +
        (selected && !isDisabled ? ' is-selected' : '') +
        (isDisabled ? ' is-disabled' : '');
      return (
        '<button type="button" class="' + classes + '" data-pdp-addon="' + escapeAttr(addon.handle) + '" aria-pressed="' + (selected && !isDisabled ? 'true' : 'false') + '"' + (isDisabled ? ' aria-disabled="true"' : '') + '>' +
          '<div class="pdp-config__addon-img-wrap">' +
            (img ? '<img src="' + img + '" alt="' + escapeAttr(name) + '" loading="lazy">' : '') +
          '</div>' +
          '<div class="pdp-config__addon-body">' +
            '<div class="pdp-config__addon-row">' +
              '<div class="pdp-config__addon-title">' + escapeHtml(name) + '</div>' +
              '<div class="pdp-config__addon-price">' + priceHtml + '</div>' +
            '</div>' +
            (desc ? '<div class="pdp-config__addon-desc">' + escapeHtml(desc) + '</div>' : '') +
            (note ? '<div class="pdp-config__addon-note">' + escapeHtml(note) + '</div>' : '') +
          '</div>' +
          '<div class="pdp-config__addon-check" aria-hidden="true"></div>' +
        '</button>'
      );
    }).join('');

    this.el.addons.innerHTML = items;
    this.el.addons.querySelectorAll('[data-pdp-addon]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-disabled')) return;
        const h = btn.getAttribute('data-pdp-addon');
        if (self.state.addons[h]) {
          delete self.state.addons[h];
        } else {
          self.state.addons[h] = true;
        }
        self.renderAddons();
        self.renderCart();
        self.renderInfo();
        self.focusRenderedControl('data-pdp-addon', h);
      });
    });
  };

  PDPConfigurator.prototype.matchAddonVariant = function (addon) {
    if (!addon.variants || addon.variants.length === 0) return null;
    if (addon.variants.length === 1) return addon.variants[0];
    const size = this.state.size;
    if (!size) return addon.variants[0];

    const normTarget = normalizeSize(size).toLowerCase();
    const match = addon.variants.find(v => {
      const vTitle = normalizeSize(v.title).toLowerCase();
      const vOpt1 = normalizeSize(v.option1).toLowerCase();
      return vOpt1 === normTarget || vTitle.indexOf(normTarget) !== -1;
    });
    return match || addon.variants[0];
  };

  PDPConfigurator.prototype.autoMatchAddonSizes = function () {
    // Triggers re-evaluation on next render via matchAddonVariant
  };

  PDPConfigurator.prototype.renderPayOptions = function () {
    const self = this;
    const product = this.currentProduct();
    const variant = this.currentVariant();
    if (!variant) {
      this.el.payOptions.innerHTML = '';
      return;
    }
    const total = this.computeTotalCents();
    const months = this.data.settings.affirm_months || 12;
    const monthlyAffirm = Math.round(total / months);
    const block = this.crateBlockByHandle[product.handle] || {};
    const isRentable = block.rentable === true;

    const monthlyRentEstimate = Math.round(total / 24);

    // If current payment is rent but the new crate isn't rentable, reset to full
    if (this.state.paymentMethod === 'rent' && !isRentable) {
      this.state.paymentMethod = 'full';
    }

    const options = [
      { id: 'full', label: 'Pay in full', sub: formatMoney(total), note: 'Save the most' },
      { id: 'affirm', label: 'Affirm', sub: formatMoney(monthlyAffirm) + '/mo', note: '0% APR · ' + months + ' mo' }
    ];
    if (isRentable) {
      options.push({ id: 'rent', label: 'Rent', sub: formatMoney(monthlyRentEstimate) + '/mo', note: 'Cancel anytime' });
    }

    this.el.payOptions.className = 'pdp-config__pay-options' + (isRentable ? ' pdp-config__pay-options--3' : '');

    this.el.payOptions.innerHTML = options.map(function (opt) {
      const selected = self.state.paymentMethod === opt.id;
      return (
        '<button type="button" class="pdp-config__pay-option' + (selected ? ' is-selected' : '') + '" data-pdp-pay="' + opt.id + '" aria-pressed="' + (selected ? 'true' : 'false') + '">' +
          '<div class="pdp-config__pay-label">' + escapeHtml(opt.label) + '</div>' +
          '<div class="pdp-config__pay-sub">' + escapeHtml(opt.sub) + '</div>' +
          '<div class="pdp-config__pay-note">' + escapeHtml(opt.note) + '</div>' +
        '</button>'
      );
    }).join('');

    this.el.payOptions.querySelectorAll('[data-pdp-pay]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.paymentMethod = btn.getAttribute('data-pdp-pay');
        self.renderPayOptions();
        self.renderCart();
        self.focusRenderedControl('data-pdp-pay', self.state.paymentMethod);
      });
    });

    if (this.el.rentStrip) {
      this.el.rentStrip.hidden = this.state.paymentMethod !== 'rent';
    }
  };

  PDPConfigurator.prototype.renderGallery = function () {
    const product = this.currentProduct();
    const media = filterMediaByColor(product, this.state.color);
    const self = this;

    if (media.length === 0) {
      this.el.mainImage.removeAttribute('src');
      this.el.thumbs.innerHTML = '';
      this.el.thumbs.style.display = 'none';
      return;
    }
    // Clamp to a valid index; defaults to 0 (first filtered image) on fresh renders
    let idx = this.state.activeMediaIndex;
    if (typeof idx !== 'number' || idx < 0 || idx >= media.length) idx = 0;
    const main = media[idx];
    this.el.mainImage.src = main.src;
    this.el.mainImage.alt = product.title;

    // Thumbnails only make sense with 2+ images
    this.el.thumbs.style.display = media.length >= 2 ? 'flex' : 'none';

    this.el.thumbs.innerHTML = media.map(function (m, i) {
      return (
        '<button type="button" class="pdp-config__thumb' + (i === idx ? ' is-active' : '') + '" data-pdp-thumb="' + i + '" aria-label="View ' + escapeAttr(shortCrateName(product)) + ' image ' + (i + 1) + ' of ' + media.length + '"' + (i === idx ? ' aria-current="true"' : '') + '>' +
          '<img loading="lazy" src="' + m.src + '" alt="">' +
        '</button>'
      );
    }).join('');

    this.el.thumbs.querySelectorAll('[data-pdp-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.activeMediaIndex = parseInt(btn.getAttribute('data-pdp-thumb'), 10);
        self.renderGallery();
        self.focusRenderedControl('data-pdp-thumb', self.state.activeMediaIndex);
      });
    });
  };

  PDPConfigurator.prototype.currentVariant = function () {
    const product = this.currentProduct();
    return findVariant(product, this.state.size, this.state.color);
  };

  PDPConfigurator.prototype.selectedAddonVariants = function () {
    const self = this;
    return Object.keys(this.state.addons).map(function (handle) {
      const addon = self.data.addons.find(a => a.handle === handle);
      if (!addon) return null;
      return { addon: addon, variant: self.matchAddonVariant(addon) };
    }).filter(x => x && x.variant);
  };

  PDPConfigurator.prototype.computeTotalCents = function () {
    const variant = this.currentVariant();
    let total = variant ? discountedCents(variant) : 0;
    this.selectedAddonVariants().forEach(function (entry) {
      total += discountedCents(entry.variant);
    });
    return total;
  };

  PDPConfigurator.prototype.computeMsrpCents = function () {
    const variant = this.currentVariant();
    let total = variant ? variant.price : 0;
    this.selectedAddonVariants().forEach(function (entry) {
      total += entry.variant.price;
    });
    return total;
  };

  PDPConfigurator.prototype.renderCart = function () {
    const product = this.currentProduct();
    const variant = this.currentVariant();
    const msrp = this.computeMsrpCents();
    const total = this.computeTotalCents();
    const savings = msrp - total;
    const threshold = (this.data.settings.free_shipping_threshold || 0) * 100;
    const isRent = this.state.paymentMethod === 'rent';
    const isAffirm = this.state.paymentMethod === 'affirm';
    const rentMonthly = Math.round(total / 24);
    const affirmMonths = this.data.settings.affirm_months || 12;
    const affirmMonthly = Math.round(total / affirmMonths);

    // Fi Collar banner — shown when free gift is enabled AND the crate is a required product
    if (this.el.cartFi) {
      const giftEnabled = this.data.settings.free_gift_enabled === true;
      const requiredIds = this.data.settings.free_gift_required_product_ids || [];
      const productQualifies = !!product && requiredIds.indexOf(product.id) !== -1;
      const showGift = giftEnabled && productQualifies;
      this.el.cartFi.hidden = !showGift;
      this.el.cartFi.style.display = showGift ? '' : 'none';

      if (this.el.cartFiIcon) {
        const giftImg = this.data.settings.free_gift_image;
        // Icon content keys off the image URL alone — banner visibility is handled above
        if (giftImg) {
          this.el.cartFiIcon.innerHTML = '<img src="' + giftImg + '" alt="Free gift" style="width:24px;height:24px;object-fit:contain;">';
          this.el.cartFiIcon.style.display = '';
        } else {
          this.el.cartFiIcon.innerHTML = '';
          this.el.cartFiIcon.style.display = 'none';
        }
      }
    }

    const lines = [];
    if (variant) {
      const sizeLabel = this.state.size ? this.state.size + ' · ' : '';
      lines.push(
        '<li class="pdp-config__cart-line">' +
          '<span class="pdp-config__cart-line-name">' + escapeHtml(shortCrateName(product)) + '<br><span class="pdp-config__cart-line-meta">' + sizeLabel + escapeHtml(this.state.color || '') + '</span></span>' +
          '<span class="pdp-config__cart-line-price">' + formatMoney(discountedCents(variant)) + '</span>' +
        '</li>'
      );
    }
    this.selectedAddonVariants().forEach(function (entry) {
      lines.push(
        '<li class="pdp-config__cart-line pdp-config__cart-line--addon">' +
          '<span class="pdp-config__cart-line-name">' + escapeHtml(entry.addon.title) + '</span>' +
          '<span class="pdp-config__cart-line-price">+' + formatMoney(discountedCents(entry.variant)) + '</span>' +
        '</li>'
      );
    });
    this.el.cartLines.innerHTML = lines.join('');

    if (isRent) {
      this.el.cartTotalLabel.textContent = 'Monthly';
      this.el.cartMsrp.innerHTML = '';
      this.el.cartTotal.textContent = formatMoney(rentMonthly) + '/mo';
    } else if (isAffirm) {
      this.el.cartTotalLabel.textContent = 'Monthly';
      this.el.cartMsrp.innerHTML = '';
      this.el.cartTotal.textContent = formatMoney(affirmMonthly) + '/mo';
    } else {
      this.el.cartTotalLabel.textContent = 'Total';
      this.el.cartMsrp.innerHTML = msrp > total ? '<s>' + formatMoney(msrp) + '</s>' : '';
      this.el.cartTotal.textContent = formatMoney(total);
    }
    if (isAffirm) {
      this.el.cartSavings.textContent = '0% APR · ' + affirmMonths + ' months';
    } else if (!isRent && savings > 0) {
      this.el.cartSavings.textContent = 'You save ' + formatMoney(savings);
    } else {
      this.el.cartSavings.textContent = '';
    }

    const FREE_SHIP_THRESHOLD = 50000; // cents ($500)

    // Shipping progress bar — fill reflects progress toward the threshold
    if (this.el.shippingFill) {
      const pct = Math.min(100, (total / FREE_SHIP_THRESHOLD) * 100);
      this.el.shippingFill.style.width = pct + '%';
    }

    // Cart micro badges — 2x2 grid: 30-Day Trial, Lifetime Warranty, free shipping, lead time
    if (this.el.cartMicro) {
      let shipStatus, shipColor;
      if (total >= FREE_SHIP_THRESHOLD) {
        shipStatus = '✓ Free shipping unlocked';
        shipColor = 'var(--pdp-black)';
      } else {
        const remaining = FREE_SHIP_THRESHOLD - total;
        const remainingStr = (remaining / 100).toLocaleString('en-US', {
          style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2
        });
        shipStatus = '— ' + remainingStr + ' more for free shipping';
        shipColor = 'var(--pdp-gray-dark)';
      }
      const shipLabel = variant && typeof variant.shipping_label === 'string'
        ? variant.shipping_label.trim()
        : '';
      const leadTime = '✓ Ships in ' + (shipLabel || '2-4 days');
      this.el.cartMicro.innerHTML =
        '<span>✓ 30-Day Trial</span>' +
        '<span>✓ Lifetime Warranty</span>' +
        '<span style="color:' + shipColor + '">' + escapeHtml(shipStatus) + '</span>' +
        '<span>' + escapeHtml(leadTime) + '</span>';
    }

    const isAvailable = variant && variant.available;
    this.el.atcBtn.disabled = !isAvailable;
    if (!isAvailable) {
      this.el.atcLabel.textContent = 'Unavailable';
    } else if (isRent) {
      this.el.atcLabel.textContent = 'Start Rental — ' + formatMoney(rentMonthly) + '/mo';
    } else if (isAffirm) {
      this.el.atcLabel.textContent = 'Add to Cart — ' + formatMoney(affirmMonthly) + '/mo';
    } else {
      this.el.atcLabel.textContent = 'Add to Cart — ' + formatMoney(total);
    }
  };

  PDPConfigurator.prototype.handleBreedSearch = function () {
    if (!this.breeds) {
      this.el.breedResult.textContent = 'Loading breed data…';
      return;
    }
    const query = (this.el.breedInput.value || '').trim().toLowerCase();
    if (!query) {
      this.el.breedResult.textContent = 'Please enter a breed name.';
      return;
    }
    const product = this.currentProduct();
    const availableSizes = getSizeValues(product).map(normalizeSize);

    const breeds = this.breeds;
    const exact = breeds.find(b => (b['Dog breed'] || '').toLowerCase() === query);
    const aliasMatch = !exact && breeds.find(b => Array.isArray(b.Aliases) && b.Aliases.some(a => a.toLowerCase() === query));
    const partial = !exact && !aliasMatch && breeds.find(b => (b['Dog breed'] || '').toLowerCase().includes(query));
    const partialAlias = !exact && !aliasMatch && !partial && breeds.find(b => Array.isArray(b.Aliases) && b.Aliases.some(a => a.toLowerCase().includes(query)));
    const match = exact || aliasMatch || partial || partialAlias;

    if (!match) {
      this.el.breedResult.textContent = 'No match for "' + this.el.breedInput.value + '". Try the full breed name.';
      return;
    }

    const rawSize = match['Impact Dog Crate Size'] || '';
    if (/varies/i.test(rawSize) || /mix/i.test(query)) {
      this.el.breedResult.textContent = '🤔 Crate size for mixed breeds can vary. Chat with an expert for help.';
      return;
    }

    const recommended = extractSizeFromBreedString(rawSize);
    if (recommended && availableSizes.indexOf(normalizeSize(recommended)) !== -1) {
      this.state.size = recommended;
      this.autoMatchAddonSizes();
      this.renderSizeStep();
      this.renderAddons();
      this.renderCart();
      this.renderInfo();
      this.el.breedResult.innerHTML = '✓ Recommended for <strong>' + escapeHtml(match['Dog breed']) + '</strong>: ' + escapeHtml(rawSize);
    } else {
      this.el.breedResult.innerHTML = 'Recommended size for <strong>' + escapeHtml(match['Dog breed']) + '</strong> (' + escapeHtml(rawSize) + ') isn\'t available in this crate model.';
    }
  };

  PDPConfigurator.prototype.addToCart = async function () {
    const variant = this.currentVariant();
    if (!variant) {
      this.showCartError('Please complete your selection.');
      return;
    }
    const items = [{ id: variant.id, quantity: 1 }];
    this.selectedAddonVariants().forEach(function (entry) {
      items.push({ id: entry.variant.id, quantity: 1 });
    });

    this.el.atcBtn.disabled = true;
    this.el.atcLabel.textContent = 'Adding…';
    this.hideCartError();

    try {
      const response = await (window.CartBenefits?.fetch || window.fetch)('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      });
      const addedItems = await parseCartResponse(response);
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        try {
          if (typeof cartDrawer.update === 'function') {
            const didRefresh = await cartDrawer.update();
            if (didRefresh === false) throw new Error('Cart drawer refresh failed');
          }
          if (!cartDrawer.isOpen && typeof cartDrawer.open === 'function') {
            cartDrawer.returnFocusTarget = this.el.atcBtn;
            await cartDrawer.open();
          } else if (!cartDrawer.isOpen && cartDrawer.classList) {
            cartDrawer.classList.add('is-open');
            cartDrawer.removeAttribute('hidden');
          }

          const product = this.currentProduct();
          const addOnText = items.length > 1 ? ' and selected add-ons' : '';
          const successMessage = (product ? product.title : 'Product') + addOnText + ' added to cart.';
          if (typeof cartDrawer.announceStatus === 'function') {
            cartDrawer.announceStatus(successMessage);
          } else {
            this.announceCartStatus(successMessage);
          }
        } catch (refreshError) {
          const refreshMessage = 'Your items were added, but the cart display could not refresh. Please open the cart to confirm.';
          console.error('[PDP] Cart refresh failed after items were added', refreshError);
          this.showCartError(refreshMessage);
          if (cartDrawer.isOpen && typeof cartDrawer.announceStatus === 'function') {
            cartDrawer.announceStatus(refreshMessage);
          }
        }
      } else {
        this.announceCartStatus('Your selected items were added to the cart.');
      }

      // Let other cart integrations (including the free-gift synchronizer) react only
      // after the primary cart UI has finished its own refresh.
      if (!window.CartBenefits) document.dispatchEvent(new CustomEvent('cart:updated', { detail: addedItems }));
    } catch (err) {
      console.error('[PDP] Add to cart failed', err);
      this.showCartError((err && err.description) || (err && err.message) || 'Couldn\'t add to cart. Please try again.');
    } finally {
      this.el.atcBtn.disabled = false;
      this.renderCart();
    }
  };

  PDPConfigurator.prototype.showCartError = function (msg) {
    this.el.cartError.textContent = msg;
    this.el.cartError.hidden = false;
  };
  PDPConfigurator.prototype.hideCartError = function () {
    this.el.cartError.textContent = '';
    this.el.cartError.hidden = true;
  };

  PDPConfigurator.prototype.announceCartStatus = function (message) {
    let status = this.root.querySelector('[data-pdp-cart-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      status.setAttribute('data-pdp-cart-status', '');
      this.root.appendChild(status);
    }
    status.textContent = '';
    window.requestAnimationFrame(function () {
      status.textContent = message;
    });
  };

  async function parseCartResponse(response) {
    let payload = {};
    try {
      payload = await response.json();
    } catch (e) {
      // A non-JSON error response is handled by the status check below.
    }
    if (!response.ok) {
      const message = payload.description || payload.message || 'Couldn\'t add to cart. Please try again.';
      const error = new Error(typeof message === 'string' ? message : 'Couldn\'t add to cart. Please try again.');
      error.description = error.message;
      throw error;
    }
    return payload;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }

  function init() {
    const root = document.querySelector('[data-pdp-config]');
    if (!root) return;
    new PDPConfigurator(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
